import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import AppLogo from "../components/app/AppLogo";

function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [cooldown, setCooldown] = useState(0); // عدد الثواني للانتظار
  const [isSubmitting, setIsSubmitting] = useState(false);

  const baseURL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cooldown > 0 || isSubmitting) {
      return; // منع الإرسال المكرر
    }

    setIsSubmitting(true); // ❗ هنا

    try {
      const response = await axios.post(baseURL + "/api/password-reset/", {
        email,
      });

      if (response.status === 200) {
        setSent(true);
        setSnackbar({
          open: true,
          message: t("Password reset email sent"),
          severity: t("success"),
        });
        setCooldown(60);
      } else {
        setSnackbar({
          open: true,
          message: t("An unexpected error occurred. Please try again later."),
          severity: t("error"),
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: t("An unexpected error occurred. Please try again later."),
        severity: t("error"),
      });
    } finally {
      setIsSubmitting(false); // ❗ هنا في كل الحالات
    }
  };

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{
        position: "relative",
        overflow: "hidden",
      }}
    >
      <img
        src="/static/images/background.gif"
        alt="Background"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
        }}
      />

      <Paper
        elevation={5}
        sx={{
          padding: 4,
          width: 350,
          textAlign: "center",
          zIndex: 1,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <AppLogo height={90} />
        </Box>
        <Typography variant="h5" mb={2}>
          {t("Forgot password")}
        </Typography>

        <Button
          variant="outlined"
          onClick={toggleLanguage}
          size="small"
          sx={{ mb: 2 }}
        >
          {i18n.language === "ar" ? "English" : "العربية"}
        </Button>

        <form onSubmit={handleSubmit}>
          <TextField
            label={t("Email")}
            variant="outlined"
            fullWidth
            margin="normal"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <Button
            type="submit"
            variant="contained"
            color="warning"
            fullWidth
            sx={{ marginTop: 2 }}
            disabled={cooldown > 0 || isSubmitting} // ❗ هنا نربطه بالـ isSubmitting
          >
            {isSubmitting
              ? t("Please wait...")
              : cooldown > 0
              ? `${t("Please wait")} (${cooldown})`
              : t("Send reset link")}
          </Button>
        </form>

        <Link
          href="/login"
          variant="body2"
          sx={{ display: "block", marginTop: 2 }}
        >
          {t("Back to login")}
        </Link>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ForgotPassword;
